import { CSSProperties, MouseEvent } from "react"
import { Theme, ToggleButton, ToggleButtonGroup } from "@mui/material"
import { styled } from "@mui/material/styles"

import Price from "components/Price"

import { netToGross } from "@secureo/common/utils/tax/taxUtils"
import { makeStyles } from "makeStyles"

import {
	DispatchProductDetailPageReducer,
	ProductDetailPageReducerState,
	setSelectedCylinderLockingMechanism,
} from "@secureo/common/reducers/productDetailPageReducer"
import { CylinderLockingMechanism } from "@secureo/common/typings/Product"

const useStyles = makeStyles()((theme: Theme) => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 200,
	},
	selectEmpty: {
		marginTop: theme.spacing(2),
	},
	paper: {
		position: "absolute",
		left: 0,
		width: "100%",
		minWidth: "220px",
		maxWidth: "450px",
		color: theme.palette.secondary.dark,
		padding: 15,
		borderRadius: "3px",
		border: "1px solid lightgrey",
		zIndex: 2,
		background: "#f7fafa",
		fontSize: "15px",
	},
	toggleButton: {
		borderRadius: 3,
		minWidth: 130,
	},
	lockTypeConfig: {
		margin: theme.spacing(1, 0),
	},
}))

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
	"& .MuiToggleButtonGroup-grouped": {
		marginRight: theme.spacing(0.7),
		padding: theme.spacing(0, 1),
		"&:not(:first-of-type)": {
			borderRadius: theme.shape.borderRadius,
			border: "1px solid grey",
		},
		"&:first-of-type": {
			borderRadius: theme.shape.borderRadius,
			border: "1px solid grey",
		},
	},
}))

const getStyles = (isSelected: boolean) => {
	const styles: CSSProperties = {
		position: "relative",
		backgroundColor: isSelected ? "#E7EEF0" : null,
		border: isSelected ? "1px solid #7abfac" : "1px solid lightgrey",
	}

	return styles
}

const LockingMechanismConfiguration = ({ reducerState, dispatch }: Props) => {
	const { classes } = useStyles()
	const {
		isLoading,
		taxRate,
		cylinderLockingMechanismProducts,
		selectedCylinderLockingMechanism,
	} = reducerState

	const onChange = (
		_event: MouseEvent<HTMLElement, globalThis.MouseEvent>,
		mechanism: CylinderLockingMechanism,
	) => {
		setSelectedCylinderLockingMechanism(dispatch, reducerState, mechanism)
	}

	const singleLockingProduct = cylinderLockingMechanismProducts?.single ?? null
	const singleLockingNetPriceCents = singleLockingProduct?.netPriceCents ?? 0
	const singleLockingGrossPriceCents = Math.round(netToGross(singleLockingNetPriceCents, taxRate))

	const simultaneousLockingProduct = cylinderLockingMechanismProducts?.simultaneous ?? null
	const simultaneousLockingNetPriceCents = simultaneousLockingProduct?.netPriceCents ?? 0
	const simultaneousLockingGrossPriceCents = Math.round(
		netToGross(simultaneousLockingNetPriceCents, taxRate),
	)

	return (
		<div className={classes.lockTypeConfig}>
			<StyledToggleButtonGroup
				size="small"
				value={"left"}
				exclusive
				onChange={onChange}
				aria-label="text alignment"
				disabled={isLoading}
			>
				{singleLockingProduct && (
					<ToggleButton
						value="single"
						aria-label="left aligned"
						className={classes.toggleButton}
						style={getStyles(selectedCylinderLockingMechanism?.mechanism === "single")}
						disabled={isLoading}
					>
						<div>
							Einzelschliessend:{" "}
							<Price priceInEuroCents={singleLockingGrossPriceCents} />
						</div>
					</ToggleButton>
				)}
				{simultaneousLockingProduct && (
					<ToggleButton
						value="simultaneous"
						aria-label="left aligned"
						className={classes.toggleButton}
						style={getStyles(
							selectedCylinderLockingMechanism?.mechanism === "simultaneous",
						)}
						disabled={isLoading}
					>
						<div>
							Gleichschliessend:{" "}
							<Price priceInEuroCents={simultaneousLockingGrossPriceCents} />
						</div>
					</ToggleButton>
				)}
			</StyledToggleButtonGroup>
		</div>
	)
}

interface Props {
	dispatch: DispatchProductDetailPageReducer
	reducerState: ProductDetailPageReducerState
}

export default LockingMechanismConfiguration
